// src/app/spinner/spinner.component.ts
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SpinnerService } from '../services/spinner.service';

@Component({
  selector: 'app-spinner',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div *ngIf="isLoading$ | async" class="spinner-overlay z-50">
      <div class="spinner-container">
        <!-- Animated Logo -->
        <div class="logo-spinner">
          <img src="./assets/img/PDA.png" alt="Loading" class="spinner-logo" />
          <div class="spinner-ring"></div>
          <div class="spinner-ring-2"></div>
        </div>
        
        <!-- Loading Text -->
        <p class="loading-text">Loading...</p>
        
        <!-- Progress Dots -->
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      z-index: 99999 !important;
      pointer-events: none;
    }

    :host > * {
      pointer-events: auto;
    }

    .spinner-overlay {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      min-height: 100vh !important;
      min-width: 100vw !important;
      max-height: 100vh !important;
      max-width: 100vw !important;
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999 !important;
      animation: fadeIn 0.3s ease;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
      inset: 0 !important;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .spinner-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.5rem;
    }

    .logo-spinner {
      position: relative;
      width: 100px;
      height: 100px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .spinner-logo {
      width: 60px;
      height: 60px;
      object-fit: contain;
      position: relative;
      z-index: 2;
      animation: pulse 2s ease-in-out infinite;
    }

    @keyframes pulse {
      0%, 100% {
        transform: scale(1);
        opacity: 1;
      }
      50% {
        transform: scale(1.1);
        opacity: 0.8;
      }
    }

    .spinner-ring {
      position: absolute;
      width: 100%;
      height: 100%;
      border: 4px solid transparent;
      border-top-color: #4f46e5;
      border-right-color: #4f46e5;
      border-radius: 50%;
      animation: spin 1.5s linear infinite;
    }

    .spinner-ring-2 {
      position: absolute;
      width: 80%;
      height: 80%;
      border: 3px solid transparent;
      border-bottom-color: #8b5cf6;
      border-left-color: #8b5cf6;
      border-radius: 50%;
      animation: spin-reverse 2s linear infinite;
    }

    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    @keyframes spin-reverse {
      0% {
        transform: rotate(360deg);
      }
      100% {
        transform: rotate(0deg);
      }
    }

    .loading-text {
      color: white;
      font-size: 1.25rem;
      font-weight: 600;
      letter-spacing: 0.05em;
      margin: 0;
      animation: fadeInOut 1.5s ease-in-out infinite;
    }

    @keyframes fadeInOut {
      0%, 100% {
        opacity: 1;
      }
      50% {
        opacity: 0.5;
      }
    }

    .loading-dots {
      display: flex;
      gap: 0.5rem;
    }

    .loading-dots span {
      width: 8px;
      height: 8px;
      background: white;
      border-radius: 50%;
      animation: bounce 1.4s ease-in-out infinite;
    }

    .loading-dots span:nth-child(1) {
      animation-delay: 0s;
    }

    .loading-dots span:nth-child(2) {
      animation-delay: 0.2s;
    }

    .loading-dots span:nth-child(3) {
      animation-delay: 0.4s;
    }

    @keyframes bounce {
      0%, 80%, 100% {
        transform: translateY(0);
        opacity: 0.5;
      }
      40% {
        transform: translateY(-12px);
        opacity: 1;
      }
    }

    /* Responsive adjustments */
    @media (max-width: 640px) {
      .logo-spinner {
        width: 80px;
        height: 80px;
      }

      .spinner-logo {
        width: 50px;
        height: 50px;
      }

      .loading-text {
        font-size: 1rem;
      }
    }
  `]
})
export class SpinnerComponent {
  isLoading$: any;
  constructor(public spinnerService: SpinnerService) {
    this.isLoading$ = this.spinnerService.isLoading$;
    // Debug: Log loading state changes and control body scroll
    this.isLoading$.subscribe((loading: boolean) => {
      console.log('Spinner component - isLoading changed to:', loading);
      // Disable/enable body scroll based on loading state
      if (loading) {
        // Scroll to top instantly - multiple methods for compatibility
        window.scrollTo(0, 0);
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;

        // Lock scroll immediately
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.top = '0';
      } else {
        // Restore scroll
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
      }
    });
  }

}